/**
 * Talking to the notification Worker.
 *
 * The device key it issues is the only thing standing between this phone's
 * schedule and anyone else's, so it lives in IndexedDB rather than
 * localStorage: same origin protection, but it survives alongside the rest of
 * the app's state and is wiped by the same sign-out.
 */
import { WORKER_URL } from "@/config";
import { META_STORE, withStore } from "@/store/db";

/** Swallows a failure the caller has already accounted for. */
function ignore(): void {}

const DEVICE_KEY = "push-device";

export interface Registration {
  deviceId: string;
  key: string;
}

/** A show to watch for new episodes. An id and a title; not a secret. */
export interface FollowedShow {
  id: number;
  title: string;
}

export async function readRegistration(): Promise<Registration | null> {
  try {
    const [value] = await withStore<Registration | undefined>(
      META_STORE,
      "readonly",
      (store) => store.get(DEVICE_KEY) as IDBRequest<Registration | undefined>,
    );
    return value ?? null;
  } catch {
    return null;
  }
}

async function writeRegistration(registration: Registration | null): Promise<void> {
  // Split rather than a ternary: delete and put resolve to different request
  // types, and the union of them does not satisfy withStore's signature.
  if (registration === null) {
    await withStore(META_STORE, "readwrite", (store) => store.delete(DEVICE_KEY));
    return;
  }
  await withStore(META_STORE, "readwrite", (store) => store.put(registration, DEVICE_KEY));
}

function auth(registration: Registration): string {
  return `Bearer ${registration.deviceId}.${registration.key}`;
}

/**
 * Exchanges the Simkl token for a device key.
 *
 * This is the only moment the token leaves the app for anywhere but Simkl.
 * The Worker checks it against Simkl and discards it — R21 — so what comes
 * back is a credential that can do nothing except manage this device's
 * notification schedule.
 */
export async function register(
  token: string,
  subscription: PushSubscription,
  timezone: string,
  quiet: { from: number; to: number },
): Promise<Registration> {
  const response = await fetch(`${WORKER_URL}/register`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      token,
      subscription: subscription.toJSON(),
      timezone,
      quietFrom: quiet.from,
      quietTo: quiet.to,
    }),
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error ?? "Could not register for notifications");
  }
  const registration = (await response.json()) as Registration;
  await writeRegistration(registration);
  return registration;
}

export interface Queued {
  shows: number;
  queued: number;
}

/**
 * Replaces the shows this device follows.
 *
 * Episodes are deliberately not sent. The Worker reads schedules from Simkl
 * itself, which is what lets a season announced while the app sits unopened
 * still reach the phone — the app uploading episodes could only ever describe
 * what it knew when it was last opened.
 */
export async function putShows(
  registration: Registration,
  shows: readonly FollowedShow[],
): Promise<Queued> {
  const response = await fetch(`${WORKER_URL}/shows`, {
    method: "PUT",
    headers: { "content-type": "application/json", authorization: auth(registration) },
    body: JSON.stringify({ shows }),
  });
  if (!response.ok) throw new Error("Could not upload the show list");
  return (await response.json()) as Queued;
}

/** What the Worker holds, for the settings screen to say out loud. */
export async function readQueued(registration: Registration): Promise<Queued> {
  const response = await fetch(`${WORKER_URL}/schedule`, {
    headers: { authorization: auth(registration) },
  });
  if (!response.ok) throw new Error("Could not read the schedule");
  return (await response.json()) as Queued;
}

/** Quiet hours live on the Worker: it is what decides whether a send happens. */
export async function updateDevice(
  registration: Registration,
  timezone: string,
  quiet: { from: number; to: number },
): Promise<void> {
  const response = await fetch(`${WORKER_URL}/register`, {
    method: "PATCH",
    headers: { "content-type": "application/json", authorization: auth(registration) },
    body: JSON.stringify({ timezone, quietFrom: quiet.from, quietTo: quiet.to }),
  });
  if (!response.ok) throw new Error("Could not save quiet hours");
}

export async function deregister(registration: Registration): Promise<void> {
  // The local record goes first. If the network call fails the device is
  // already unusable here, and the Worker's entries expire on their own.
  await writeRegistration(null);
  await fetch(`${WORKER_URL}/register`, {
    method: "DELETE",
    headers: { authorization: auth(registration) },
  }).catch(ignore);
}
